import React, { useState } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { LayoutDashboard, Ticket, Lock, FolderKanban, Users, LogOut, Monitor, Server, TrendingUp, CheckSquare, DollarSign, FileText, CreditCard, TrendingDown, ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PortalLayout = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [financeiroOpen, setFinanceiroOpen] = useState(window.location.pathname.includes('/financeiro'));

  const handleLogout = () => {
    localStorage.removeItem('user');
    toast({
      title: 'Logout realizado',
      description: 'Até logo!',
    });
    navigate('/login');
  };

  const menuItems = [
    { path: '/portal/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'tecnico', 'user'] },
    { path: '/portal/chamados', icon: Ticket, label: 'Chamados', roles: ['admin', 'tecnico', 'user'] },
    { path: '/portal/clientes', icon: Users, label: 'Clientes', roles: ['admin', 'tecnico'] },
    { path: '/portal/leads', icon: TrendingUp, label: 'CRM - Leads', roles: ['admin'] },
    { path: '/portal/senhas', icon: Lock, label: 'Senhas', roles: ['admin', 'tecnico'] },
    { path: '/portal/projetos', icon: FolderKanban, label: 'Projetos', roles: ['admin', 'tecnico'] },
    { path: '/portal/tasks', icon: CheckSquare, label: 'Tarefas', roles: ['admin', 'tecnico'] },
    { 
      label: 'Financeiro', 
      icon: DollarSign, 
      roles: ['admin'],
      isParent: true,
      children: [
        { path: '/portal/financeiro', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/portal/financeiro/contratos', icon: CreditCard, label: 'Contratos' },
        { path: '/portal/financeiro/receitas', icon: FileText, label: 'Receitas' },
        { path: '/portal/financeiro/despesas', icon: TrendingDown, label: 'Despesas' },
        { path: '/portal/financeiro/configuracoes', icon: Settings, label: 'Configurações' },
      ]
    },
    { path: '/portal/usuarios', icon: Users, label: 'Usuários', roles: ['admin'] },
  ];

  const externalLinks = [
    { url: 'http://noc.nexti.com.vc', icon: Monitor, label: 'Monitoramento', roles: ['admin', 'tecnico'] },
    { url: 'https://rmm.nexti.com.vc/login', icon: Server, label: 'RMM', roles: ['admin', 'tecnico'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user.role || 'user')
  );

  const filteredExternalLinks = externalLinks.filter(item => 
    item.roles.includes(user.role || 'user')
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/portal/dashboard" className="flex items-center">
            <img 
              src="/nexti-logo.png" 
              alt="NexTI" 
              className="h-10 w-auto"
              style={{ filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.9)) drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7))' }}
            />
          </Link>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-900">{user.name || 'Usuário'}</div>
              <div className="text-xs text-gray-600">
                {user.role === 'admin' ? 'Administrador' : user.role === 'tecnico' ? 'Técnico' : 'Usuário'}
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-gray-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white min-h-[calc(100vh-73px)] border-r">
          <nav className="p-4 space-y-1">
            {filteredMenuItems.map((item) => {
              const IconComponent = item.icon;
              
              // Menu com submenu (Financeiro)
              if (item.isParent) {
                const isFinanceiroActive = window.location.pathname.includes('/financeiro');
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => setFinanceiroOpen(!financeiroOpen)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isFinanceiroActive
                          ? 'bg-[#4A2B7F] text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {financeiroOpen ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Submenu */}
                    {financeiroOpen && (
                      <div className="mt-1 ml-4 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isActive = window.location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors text-sm ${
                                isActive
                                  ? 'bg-[#6B4DA1] text-white'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <ChildIcon className="w-4 h-4" />
                              <span>{child.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Menu simples
              const isActive = window.location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#4A2B7F] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <IconComponent className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}

            {/* External Links */}
            {filteredExternalLinks.length > 0 && (
              <>
                <div className="border-t my-2"></div>
                {filteredExternalLinks.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <a
                      key={item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-gray-700 hover:bg-gray-100"
                    >
                      <IconComponent className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </a>
                  );
                })}
              </>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PortalLayout;